import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Wallet, Trash2, PlugZap, Info, Eye, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { getPortfolio, deleteHolding, getCompanies } from "../lib/api";
import { Panel, PageTitle, SectionHeading, SectionDivider, MetricCard } from "../components/Panel";
import { Reveal } from "../components/Reveal";
import { StaggerGrid, StaggerItem } from "../components/Motion";
import { AddHoldingPanel } from "../components/portfolio/AddHoldingPanel";
import { ResearchGrid } from "../components/portfolio/ResearchGrid";
import { formatINR, formatPercent, signColor } from "../lib/format";
import { DataState, EmptyState } from "../components/DataState";
import { SkeletonBlock } from "../components/LoadingSkeleton";

const ALLOC_COLORS = ["#F59E0B", "#10B981", "#38BDF8", "#A855F7", "#EC4899", "#FB7185", "#818CF8"];

export default function Portfolio() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [universe, setUniverse] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const load = () => { setError(false); getPortfolio().then(setData).catch(() => setError(true)); };
  useEffect(() => { load(); getCompanies().then((d) => setUniverse(d.companies)).catch(() => {}); }, []);

  const remove = async (id, ticker) => {
    const next = await deleteHolding(id);
    setData(next);
    toast.success(`Removed ${ticker} from your research portfolio`);
  };

  if (error) return <DataState title="Portfolio intelligence is temporarily unavailable" onRetry={load} />;
  if (!data) return <PortfolioSkeleton />;

  const s = data.summary;
  const uniqueTickers = [...new Set(data.holdings.map((h) => h.ticker))];

  return (
    <div className="space-y-6">
      <PageTitle
        kicker="Portfolio Intelligence"
        title="Your portfolio, through the LONGVIEW."
        subtitle="Track your holdings, explore the businesses behind them, and build a clearer picture of your portfolio."
      />

      {/* Portfolio simulator status */}
      <div className="flex items-center gap-3 rounded-lg border border-indigo-500/25 bg-indigo-500/[0.06] p-4">
        <PlugZap className="h-5 w-5 shrink-0 text-indigo-300" />
        <div>
          <p className="flex items-center gap-2 text-sm font-medium text-indigo-100">
            Portfolio Simulator
            <span className="rounded border border-indigo-400/30 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-indigo-300">Research Only</span>
          </p>
          <p className="mt-0.5 text-xs text-indigo-200/70">Holdings are tracked for research and simulation purposes. No brokerage account is connected and no real money is involved.</p>
        </div>
      </div>

      {/* Overview */}
      <StaggerGrid className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StaggerItem><MetricCard data-testid="portfolio-summary-card" label="Total Value" value={formatINR(s.total_value)} /></StaggerItem>
        <StaggerItem><MetricCard label="Invested" value={formatINR(s.total_invested)} /></StaggerItem>
        <StaggerItem><MetricCard label="Total P&L" value={formatINR(s.pnl)} sub={formatPercent(s.pnl_percent)} tone={s.pnl > 0 ? "positive" : s.pnl < 0 ? "negative" : "neutral"} /></StaggerItem>
        <StaggerItem><MetricCard label="Holdings" value={s.holdings_count} sub={`${uniqueTickers.length} compan${uniqueTickers.length === 1 ? "y" : "ies"}`} /></StaggerItem>
      </StaggerGrid>

      {data.holdings.length === 0 ? (
        <>
          <Reveal>
            <EmptyState
              icon={Wallet}
              title="Your research portfolio is empty"
              detail="Add companies you're researching to build a personal LONGVIEW workspace."
              action={
                <button
                  data-testid="portfolio-empty-add-btn"
                  onClick={() => setAddOpen(true)}
                  className="lv-press inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-2 font-mono text-xs font-medium text-amber-300 transition-colors hover:bg-amber-500/20"
                >
                  <PlusCircle className="h-3.5 w-3.5" /> Add your first holding
                </button>
              }
            />
          </Reveal>
          <AnimatePresence>
            {addOpen && <AddHoldingPanel universe={universe} onAdded={setData} onClose={() => setAddOpen(false)} />}
          </AnimatePresence>
        </>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Holdings terminal */}
          <div className="space-y-3 lg:col-span-8">
            <SectionHeading kicker="Holdings Terminal" title="Your research positions" icon={Wallet} />

            <AnimatePresence>
              {addOpen && <AddHoldingPanel universe={universe} onAdded={setData} onClose={() => setAddOpen(false)} />}
            </AnimatePresence>
            {!addOpen && (
              <button
                type="button"
                data-testid="portfolio-add-holding-btn"
                onClick={() => setAddOpen(true)}
                className="lv-press flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-white/[0.14] py-3 font-mono text-xs text-zinc-400 transition-colors hover:border-amber-500/40 hover:text-amber-200"
              >
                <PlusCircle className="h-4 w-4" /> Add a holding
              </button>
            )}

            <StaggerGrid data-testid="portfolio-holdings-table" className="space-y-2.5">
              {data.holdings.map((h) => <StaggerItem key={h.id}><HoldingRow h={h} onRemove={remove} /></StaggerItem>)}
            </StaggerGrid>

            <p className="mt-1 flex items-center gap-1.5 text-xs text-zinc-600">
              <Info className="h-3.5 w-3.5" /> Positions are added at the latest verified market price — this is a simulator, not a live brokerage.
            </p>
          </div>

          {/* Allocation */}
          <div className="space-y-4 lg:col-span-4">
            <SectionHeading kicker="Diversification" title="Sector allocation" />
            <Panel className="p-5">
              <div className="mb-4 flex h-3 overflow-hidden rounded-full border border-white/[0.06]">
                {data.allocation.map((a, i) => (
                  <div key={a.sector} style={{ width: `${a.percent}%`, background: ALLOC_COLORS[i % ALLOC_COLORS.length] }} />
                ))}
              </div>
              <div className="space-y-3">
                {data.allocation.map((a, i) => (
                  <div key={a.sector} className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: ALLOC_COLORS[i % ALLOC_COLORS.length] }} />
                      <span className="truncate text-xs text-zinc-300">{a.sector}</span>
                    </span>
                    <span className="shrink-0 font-mono text-xs text-zinc-400">{a.percent}%</span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {data.holdings.length > 0 && (
        <>
          <SectionDivider />
          <Reveal>
            <SectionHeading kicker="Portfolio Intelligence" title="Companies you're researching" />
            <ResearchGrid tickers={uniqueTickers} />
          </Reveal>
        </>
      )}
    </div>
  );
}

const HoldingRow = ({ h, onRemove }) => {
  const rowId = `${h.ticker.toLowerCase()}-${h.id.slice(0, 6)}`;
  return (
    <div
      data-testid={`portfolio-holding-row-${rowId}`}
      className="group flex flex-col gap-3 rounded-lg border border-white/[0.06] bg-[#0B0C0E] p-4 transition-colors duration-200 hover:border-amber-500/25 sm:grid sm:grid-cols-[2fr_0.6fr_0.9fr_0.9fr_1fr_1.1fr_auto] sm:items-center sm:gap-4 sm:p-3.5"
    >
      <Link to={`/company/${h.ticker}`} className="flex min-w-0 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-white/10 bg-[#14171C] font-mono text-[10px] font-bold text-zinc-300">{h.ticker.slice(0, 4)}</span>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-white">{h.name}</div>
          <div className="truncate font-mono text-[10px] uppercase tracking-wider text-zinc-500">{h.ticker} · {h.sector}</div>
        </div>
      </Link>
      <Stat label="Qty" value={h.quantity} />
      <Stat label="Avg" value={formatINR(h.avg_price)} />
      <Stat label="Price" value={formatINR(h.price)} valueClass="text-white" />
      <Stat label="Value" value={formatINR(h.value)} valueClass="text-white" />
      <Stat label="P&L" value={<>{formatINR(h.pnl)} <span className="text-[11px]">{formatPercent(h.pnl_percent)}</span></>} valueClass={signColor(h.pnl)} />
      <div className="flex items-center justify-end gap-1 border-t border-white/[0.05] pt-2 sm:border-0 sm:pt-0">
        <Link to={`/company/${h.ticker}`} data-testid={`portfolio-view-${rowId}`} className="lv-press rounded p-1.5 text-zinc-500 transition-colors hover:text-amber-300" aria-label={`View ${h.name}`}>
          <Eye className="h-4 w-4" />
        </Link>
        <button type="button" data-testid={`portfolio-delete-${rowId}`} onClick={() => onRemove(h.id, h.ticker)} className="lv-press rounded p-1.5 text-zinc-500 transition-colors hover:text-rose-400" aria-label={`Remove ${h.name}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const Stat = ({ label, value, valueClass = "text-zinc-300" }) => (
  <div className="flex items-center justify-between sm:block sm:text-right">
    <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600 sm:hidden">{label}</span>
    <span className={`font-mono text-sm tabular-nums ${valueClass}`}>{value}</span>
  </div>
);

const PortfolioSkeleton = () => (
  <div className="space-y-6">
    <SkeletonBlock className="h-24" />
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => <SkeletonBlock key={i} className="h-24" />)}
    </div>
    <SkeletonBlock className="h-96" />
  </div>
);
